import { StrictMode } from 'react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from "@sentry/react"
import { nukeSWInDev } from './sw-dev-guard'
import './styles/tokens.css' // Basil design tokens — MUST load first (single source of truth)
import './index.css'
import './styles/navigation-fix.css' // Import navigation fix CSS
import './styles/glassmorphism.css' // Import glassmorphism styles
import './styles/enterprise.css' // Import enterprise-grade styles
import './styles/steward-mobile.css' // Import steward mobile-specific styles
import './styles/modern-flow.css' // Import Modern Flow theme styles
import './styles/comprehensive-responsive-system.css' // Import comprehensive responsive system
import './styles/responsive-design-system.css' // Import responsive design system  
import './styles/button-design-system.css' // Import button design system
import './styles/menu-dropdown-fix.css' // Import menu dropdown z-index fix
import './styles/admin-vendor-menu-fix.css' // Import admin vendor menu dropdown fix
import './styles/modal-z-index-fix.css' // Import modal z-index hierarchy fix
import './styles/vendor-orders-dropdown-fix.css' // Import vendor orders dropdown visibility fix
import './styles/scandinavian-warm.css' // Import Scandinavian Warm theme styles
import App from './App.tsx'
import { AuthProvider } from './context/AuthContext'
import { ConfirmProvider } from './context/ConfirmContext'
import OnScreenKeyboard from './components/kiosk/OnScreenKeyboard'
import { ThemeProvider } from './context/ThemeContext'
import { CartProvider } from './context/CartContext'
import { VendorConfigProvider } from './context/VendorConfigContext'
import { VendorBrandingProvider } from './context/VendorBrandingContext'
import { SocketProvider } from './context/SocketContext'
import { AutoPrintProvider } from './context/AutoPrintContext'
import { EdgeProvider } from './edge/EdgeContext'
import { UnifiedMessagingProvider } from './context/UnifiedMessagingContext'
import { ToastProvider } from './context/ToastContext'
import { NotificationProvider } from './context/NotificationContext'
import GuestSessionManager from './components/GuestSessionManager'
import { GlobalMuiOverrides } from './theme/GlobalMuiOverrides'
import { ColorModeProvider } from './context/ColorModeContext'

// Initialize Sentry with enhanced iOS debugging
Sentry.init({
  dsn: "https://d2f41b53dc4f3288f30d50aefc89fbd7@o4509690111655936.ingest.de.sentry.io/4509690117816400",
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  autoSessionTracking: true,
  beforeSend(event) {
    // Add iOS/Safari context
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    event.contexts = {
      ...event.contexts,
      device: {
        ...event.contexts?.device,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        is_ios: isIOS,
        is_safari: isSafari,
        user_agent: navigator.userAgent,
      },
    };
    
    return event;
  },
});

// Check iOS compatibility
const checkIOSCompatibility = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    // Check localStorage availability (fails in Private Browsing)
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
    } catch (e) {
      Sentry.captureException(new Error('localStorage not available - Private Browsing mode?'));
    }
    
    // Log iOS version
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match) {
      const iosVersion = `${match[1]}.${match[2]}.${match[3] || 0}`;
      Sentry.setContext("ios", {
        version: iosVersion,
        safari: /Safari/.test(navigator.userAgent),
        standalone: (window.navigator as any).standalone,
      });
    }
  }
};

checkIOSCompatibility();

// Nuke service workers in development
nukeSWInDev();

// Additional dev-only service worker cleanup
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister()));
  if ('caches' in window) caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
  console.info('[SW] nuked in DEV');
}

// Register service worker only in production; ensure it is unregistered in development
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered for PWA install:', registration);
      })
      .catch(error => {
        console.log('SW registration failed:', error);
      });
  });
} else if (!import.meta.env.PROD && 'serviceWorker' in navigator) {
  // In dev/localhost, actively remove any existing service workers and clear caches
  navigator.serviceWorker.getRegistrations().then(regs => {
    regs.forEach(reg => reg.unregister());
  });
  if ('caches' in window) {
    caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
  }
}

// Conditionally use StrictMode - disable in development due to React 18 StrictMode
// double-mounting causing duplicate synthetic event handlers in interactive components
// See: https://react.dev/reference/react/StrictMode#fixing-bugs-found-by-double-rendering-in-development
const RootWrapper = import.meta.env.PROD ? StrictMode : React.Fragment;

createRoot(document.getElementById('root')!).render(
  <RootWrapper>
    <BrowserRouter>
      <ColorModeProvider>
      <GlobalMuiOverrides>
        <ThemeProvider>
          <AuthProvider>
            <VendorConfigProvider>
              <VendorBrandingProvider>
                <ToastProvider>
                  <SocketProvider>
                  <AutoPrintProvider>
                    <UnifiedMessagingProvider>
                      <NotificationProvider>
                        <CartProvider>
                        <ConfirmProvider>
                          <GuestSessionManager>
                            <Sentry.ErrorBoundary 
                          fallback={({ error }) => (
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                              <h1>Something went wrong</h1>
                              <p>{error?.message}</p>
                              <button onClick={() => window.location.reload()}>Reload</button>
                            </div>
                          )}
                          showDialog
                        >
                          <EdgeProvider>
                            <App />
                            <OnScreenKeyboard />
                          </EdgeProvider>
                            </Sentry.ErrorBoundary>
                          </GuestSessionManager>
                        </ConfirmProvider>
                      </CartProvider>
                      </NotificationProvider>
                    </UnifiedMessagingProvider>
                  </AutoPrintProvider>
                </SocketProvider>
              </ToastProvider>
              </VendorBrandingProvider>
            </VendorConfigProvider>
          </AuthProvider>
        </ThemeProvider>
      </GlobalMuiOverrides>
      </ColorModeProvider>
    </BrowserRouter>
  </RootWrapper>,
)
